import React from "react";
import { View, Image, ViewStyle } from "react-native";
import {
  GradientButton,
  SubHeader,
  Card,
  Content,
  Spacer,
} from "../../../components";
import {
  useAppDispatch,
  useAppSelector,
  mixActions,
  Target,
  initialColor,
} from "../../../store";
import {
  ButtonFinish,
  ButtonGenerate,
  ButtonMix,
  ButtonRestart,
  ButtonSend,
  Picker,
} from "../components/MixButtons";
import {
  FieldGenerator,
  FieldMixed,
  FieldReceived,
  FieldResult,
  FieldSecret,
} from "../components/MixFields";
import { styles } from "../mixStyles";
import { useDeviceOrientation } from "@react-native-community/hooks";
import { Outlines, Sizing } from "../../../theme";

export const flexDirectionToInside = (target: Target) =>
  target === "bob" ? "row-reverse" : "row";
export const flexDirectionToOutside = (target: Target) =>
  target === "alice" ? "row-reverse" : "row";

export const useItemLayout = (flex: number = 1): ViewStyle => {
  const lockRatio = useDeviceOrientation().portrait;
  return {
    borderRadius: Outlines.borderRadius.full,
    margin: Sizing.layout.x10,
    aspectRatio: lockRatio ? flex : undefined,
    // maxHeight: lockRatio ? undefined : Sizing.layout.x80,
    flex,
  };
};

export interface TargetProps {
  target: Target;
}

export const MixScreen = () => {
  const space = "medium";

  return (
    <Content>
      <Card style={[{ flex: 1 }, styles.row, styles.card]}>
        <ButtonGenerate />
        <FieldGenerator pressGenerates={false} />
        <ButtonRestart />
      </Card>
      <Spacer />
      <View style={[{ flex: 5 }, styles.row]}>
        <View style={{ flex: 1 }}>
          <Player target="alice" />
        </View>
        <Spacer />
        <View style={{ flex: 1 }}>
          <Player target="bob" />
        </View>
      </View>
    </Content>
  );
};

const Player = ({ target }: TargetProps) => {
  const hasStarted = useAppSelector((state) => state.mix[target].hasStarted);
  return hasStarted ? (
    <PlayerControls target={target} />
  ) : (
    <PlayerStart target={target} />
  );
};

const PlayerStart = ({ target }: TargetProps) => {
  const dispatch = useAppDispatch();
  const generator: string = useAppSelector((state) => state.mix.generator);
  const name = target === "bob" ? "Bob" : "Alice";
  const image =
    target === "bob"
      ? require("../../../../assets/bob.png")
      : require("../../../../assets/alice.png");

  return (
    <Card style={[styles.card, styles.startContent, { height: "100%" }]}>
      <Spacer />
      <SubHeader text={name} />
      <Spacer />
      <Image style={styles.playerImage} source={image} resizeMode="contain" />
      <GradientButton
        outlined
        disabled={generator === initialColor}
        title={"Start"}
        style={styles.startButton}
        onPress={() => {
          dispatch(mixActions.start(target));
        }}
      />
    </Card>
  );
};

export interface RowProps {
  target: Target;
  style?: ViewStyle;
  children?: React.ReactNode;
}

export const Row = ({ target, style, children }: RowProps) => (
  <View
    style={[
      {
        flexDirection: flexDirectionToInside(target),
      },
      style,
    ]}
  >
    {children}
  </View>
);

const PlayerControls = ({ target }: TargetProps) => {
  return (
    <Card style={(styles.card, { height: "100%", flexDirection: "column" })}>
      <Row target={target} style={{ flex: 3 }}>
        <Picker target={target} />
        <FieldSecret target={target} unpickable={false} />
      </Row>
      <Row target={target} style={{ flex: 2 }}>
        <ButtonMix target={target} />
        <FieldMixed target={target} />
      </Row>
      <Row target={target} style={{ flex: 2 }}>
        <ButtonSend target={target} />
        <FieldReceived target={target} />
      </Row>
      <Row target={target} style={{ flex: 4 }}>
        <ButtonFinish target={target} />
        <FieldResult target={target} />
      </Row>
    </Card>
  );
};
